import {
  Box,
  Flex,
  Text,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td
} from '@chakra-ui/react';
import { faEdit } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ButtonIcon, OutlineCard } from '@morressier/alembic';
import { useQuery } from '@tanstack/react-query';
import React, { useCallback, useMemo, useState } from 'react';

import {
  fetchJournalConfiguredDecisionTemplates,
  fetchJournalConfiguredResponseTemplates
} from 'src/api/queries';

import ResponseTemplateFormModal from './ResponseTemplateFormModal';

interface ResponseTemplatesProps {
  journalId: string;
}

interface ModalState {
  isOpen: boolean;
  decisionType: string;
  decisionLabel: string;
}

interface DecisionOption {
  type: string;
  label: string;
  description: string;
  subtypes: Array<{
    label: string;
    type: string;
  }>;
}

const TEMPLATE_TYPE = 'RESPONSE_TO_AUTHOR';

const INITIAL_MODAL_STATE: ModalState = {
  isOpen: false,
  decisionType: '',
  decisionLabel: ''
};

const ResponseTemplates: React.FC<ResponseTemplatesProps> = ({ journalId }) => {
  const [modal, setModal] = useState<ModalState>(INITIAL_MODAL_STATE);

  const { data: decisionOptionsData, isLoading: isDecisionOptionsLoading } =
    useQuery({
      queryKey: ['journal-configuration-response-template-options', journalId, TEMPLATE_TYPE],
      queryFn: () => fetchJournalConfiguredResponseTemplates(journalId, TEMPLATE_TYPE),
      enabled: !!journalId
    });

  const { data: configuredTemplatesData, isLoading: isConfiguredTemplatesLoading } =
    useQuery({
      queryKey: ['journal-configuration-decision-templates', journalId, TEMPLATE_TYPE],
      queryFn: () => fetchJournalConfiguredDecisionTemplates(journalId, TEMPLATE_TYPE),
      enabled: !!journalId
    });

  const decisionOptions = useMemo(
    () => decisionOptionsData?.decisions ?? [],
    [decisionOptionsData]
  );

  const configuredTemplatesByType = useMemo(() => {
    const result: Record<string, string[]> = {};
    const configuredTemplates = configuredTemplatesData?.decisionTemplates ?? [];

    for (const entry of configuredTemplates) {
      const templates = entry.templates
        ?? entry.subtypes?.flatMap(subtype => subtype.templates)
        ?? [];

      result[entry.type] = templates.map(template =>
        template.subtype
          ? `${template.label} (${template.subtype})`
          : template.label
      );
    }

    return result;
  }, [configuredTemplatesData]);

  const openModal = useCallback((decisionType: string, decisionLabel: string) => {
    setModal({
      isOpen: true,
      decisionType,
      decisionLabel
    });
  }, []);

  const closeModal = useCallback(() => {
    setModal(INITIAL_MODAL_STATE);
  }, []);

  return (
    <Flex flexDirection="column" gap={8}>
      <OutlineCard borderless paddingX={4} paddingY={6}>
        <Flex flexDirection="column" padding={2} gap={8}>
          <Text textStyle="headingSection">Pre-filled Responses to Author</Text>

          {decisionOptions.length === 0 ? (
            <OutlineCard borderless paddingX={4} paddingY={6}>
              <Flex
                flexDirection="column"
                gap={2}
                alignItems="center"
                paddingY={8}
              >
                <Text textStyle="headingGroup">No response template options</Text>
                <Text textStyle="textBody">
                  No decision response options are available for this journal.
                </Text>
              </Flex>
            </OutlineCard>
          ) : (
            <Box overflowX="auto" width="100%">
              <Table minWidth="520px">
                <Thead>
                  <Tr
                    data-testId="response-templates-table-header"
                    sx={{
                      '& th': {
                        textTransform: 'none',
                        color: 'black',
                        background: 'white'
                      }
                    }}
                  >
                    <Th p={{ base: 2, md: 4 }}>
                      <Text textStyle="headingItem" whiteSpace="nowrap">
                        Decision Type
                      </Text>
                    </Th>
                    <Th p={{ base: 2, md: 4 }}>
                      <Text textStyle="headingItem" whiteSpace="nowrap">
                        Pre-filled Responses
                      </Text>
                    </Th>
                    <Th
                      width="100px"
                      p={{ base: 2, md: 4 }}
                      textAlign="center"
                    >
                      <Text textStyle="headingItem" whiteSpace="nowrap">
                        Actions
                      </Text>
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {decisionOptions.map((decision: DecisionOption) => {
                    const configured = configuredTemplatesByType[decision.type] ?? [];

                    return (
                      <Tr
                        key={decision.type}
                        data-testId="response-templates-table-row"
                        background="white"
                        alignItems="center"
                        _last={{ borderBottom: 'none' }}
                      >
                        <Td px={{ base: 2, md: 6 }}>
                          <Text textStyle="headingItem">{decision.label}</Text>
                        </Td>
                        <Td px={{ base: 2, md: 6 }}>
                          {isDecisionOptionsLoading || isConfiguredTemplatesLoading ? (
                            <Text textStyle="textContext" color="textSubtle">
                              Loading...
                            </Text>
                          ) : configured.length === 0 ? (
                            <Text textStyle="textContext" color="textSubtle">
                              No templates configured
                            </Text>
                          ) : (
                            <Text textStyle="textContext" wordBreak="break-word">
                              {configured.join(', ')}
                            </Text>
                          )}
                        </Td>
                        <Td px={{ base: 2, md: 6 }}>
                          <ButtonIcon
                            variant="secondary"
                            size="small"
                            ariaLabel={`Configure templates for ${decision.label}`}
                            icon={<FontAwesomeIcon icon={faEdit} />}
                            onClick={() => openModal(decision.type, decision.label)}
                          />
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </Box>
          )}
        </Flex>
      </OutlineCard>

      {modal.isOpen && (
        <ResponseTemplateFormModal
          isOpen={modal.isOpen}
          journalId={journalId}
          templateType={TEMPLATE_TYPE}
          decisionType={modal.decisionType}
          decisionLabel={modal.decisionLabel}
          onClose={closeModal}
        />
      )}
    </Flex>
  );
};

export default ResponseTemplates;
